// Trip Planner Page - Thin orchestrator for modular components
// Security: No dangerouslySetInnerHTML, map security handled in MapPanel
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { cloneTrip, getTripStatus, submitPatchAnswer, addActivity, addDay, AddDayError, createTripUnlockCheckout } from '../services/api';
import { logger } from '../utils/logger';
import { useTrip } from '../hooks/useTrip';
import { useUserStatus } from '../hooks/useUserStatus';
import { 
    getPendingCheckout, 
    clearPendingCheckout, 
    isCheckoutInFlight, 
    setCheckoutInFlight, 
    clearCheckoutInFlight,
    isPendingCheckoutExpired 
} from '../utils/pendingCheckout';
import UnlockModal, { UnlockTrigger, UnlockEntrypoint } from '../components/UnlockModal';
import TripBanner from '../components/trip/TripBanner';
import TripHero from '../components/trip/TripHero';
import ItinerarySidebar from '../components/trip/ItinerarySidebar';
import MapPanel from '../components/trip/MapPanel';
import MapPanelFloating from '../components/trip/MapPanelFloating';
import ChatPanel from '../components/chat/ChatPanel';
import ShareModal from '../components/trip/ShareModal';
import StickyUnlockBar from '../components/trip/StickyUnlockBar';
import TripSummaryCard from '../components/trip/TripSummaryCard';
import TripNotFound from '../components/trip/TripNotFound';
import GeneratingView from '../components/trip/GeneratingView';
import './TripPage.css';

export default function TripPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { slug } = useParams({ strict: false }) as { slug: string };

    // Read editToken from URL query params (for shared links)
    // This is read once at mount and used for the initial trip fetch
    const urlEditToken = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('editToken') || undefined;
    }, []);

    // Hooks for data and entitlements
    // Pass urlEditToken to useTrip for shared link access (view-only)
    const { tripData, loading: tripLoading, error: tripError, fetchTrip, updateTripData } = useTrip(slug, urlEditToken);
    const { userStatus, setUserStatus, refreshStatus } = useUserStatus(slug);

    // UI state
    const [selectedDay, setSelectedDay] = useState({ cityIdx: 0, dayIdx: 0 });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [modalTrigger, setModalTrigger] = useState<UnlockTrigger>('chat');
    const [modalEntrypoint, setModalEntrypoint] = useState<UnlockEntrypoint>('cta');
    const [forceUpgradeView, setForceUpgradeView] = useState(false);
    const [checkoutCanceled, setCheckoutCanceled] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [cloning, setCloning] = useState(false);
    const [isChatOpenMobile, setIsChatOpenMobile] = useState(false);

    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 900);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Ref to open routes modal from hero route timeline (desktop)
    const openRoutesModalRef = useRef<(() => void) | null>(null);
    // Desktop floating map panel state (NOT persisted - default closed)
    const [isDesktopMapOpen, setIsDesktopMapOpen] = useState(false);
    const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
    const [focusedActivityTitle, setFocusedActivityTitle] = useState<string | undefined>(undefined);

    // Handler for "View on map" from activity cards
    const handleViewOnMap = useCallback((activityId: string, activityTitle?: string) => {
        setSelectedActivityId(activityId);
        setFocusedActivityTitle(activityTitle);
        setIsDesktopMapOpen(true);
    }, []);

    // Handler for "Show map" button (no specific activity)
    const handleToggleMap = useCallback(() => {
        if (isDesktopMapOpen) {
            setIsDesktopMapOpen(false);
            setSelectedActivityId(null);
            setFocusedActivityTitle(undefined);
        } else {
            setIsDesktopMapOpen(true);
            setFocusedActivityTitle(undefined);
        }
    }, [isDesktopMapOpen]);

    // Handler for clearing selected activity
    const handleClearSelectedActivity = useCallback(() => {
        setSelectedActivityId(null);
        setFocusedActivityTitle(undefined);
    }, []);

    // Computed: Is trip locked?
    // NOT locked if: user is premium OR has purchased this trip (or its source)
    const isTripLocked = !userStatus?.isPremium && !userStatus?.isTripUnlocked;

    // Computed: Show sticky bar?
    const showStickyBar = isMobile && isTripLocked && !isCheckoutInFlight() && !showUnlockModal;

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [patchQuestion, setPatchQuestion] = useState<string | null>(null);
    const [patchQuestionType, setPatchQuestionType] = useState<string | null>(null);
    const [editToken, setEditToken] = useState<string | null>(null);
    const [hasAnsweredPatch, setHasAnsweredPatch] = useState(false);

    // Check for stored question, questionType, and editToken from TripBuilderForm
    useEffect(() => {
        if (tripData?.trip.id) {
            const storedQuestion = sessionStorage.getItem(`trip_${tripData.trip.id}_question`);
            const storedQuestionType = sessionStorage.getItem(`trip_${tripData.trip.id}_questionType`);
            const storedEditToken = sessionStorage.getItem(`trip_${tripData.trip.id}_editToken`);
            
            if (storedQuestion) {
                setPatchQuestion(storedQuestion);
                sessionStorage.removeItem(`trip_${tripData.trip.id}_question`);
            }
            if (storedQuestionType) {
                setPatchQuestionType(storedQuestionType);
                // Keep editToken and questionType for patch submission
            }
            if (storedEditToken) {
                setEditToken(storedEditToken);
            }
        }
    }, [tripData?.trip.id]);

    // Detect if trip is still generating
    useEffect(() => {
        const status = tripData?.trip.generationStatus;
        // 'generating' = waiting for Day 1, 'partial' = Day 1 ready but Days 2+ still generating
        if (status === 'generating' || status === 'partial' || (tripData && !tripData.content)) {
            setIsGenerating(true);
        } else {
            setIsGenerating(false);
        }
    }, [tripData]);

    // Poll for trip completion when generating
    useEffect(() => {
        if (!isGenerating || !slug) return;

        const pollInterval = setInterval(async () => {
            try {
                const status = await getTripStatus(slug);
                if (status.ready) {
                    setIsGenerating(false);
                    setShowLoadingModal(false);
                    // Refetch trip data to get content
                    fetchTrip();
                } else if (status.status === 'failed') {
                    setIsGenerating(false);
                    setShowLoadingModal(false);
                }
            } catch (err) {
                logger.error('Status poll failed:', err);
            }
        }, 2000);

        return () => clearInterval(pollInterval);
    }, [isGenerating, slug, fetchTrip]);

    // Handle patch answer submission - POST to backend
    const handlePatchAnswer = useCallback(async (answer: string) => {
        if (!tripData?.trip.id || !patchQuestionType) return;
        
        setHasAnsweredPatch(true);
        setShowLoadingModal(true);

        try {
            // Submit patch to backend
            await submitPatchAnswer(tripData.trip.id, {
                questionType: patchQuestionType,
                answer,
                editToken: editToken || undefined,
            });

            // Clear session storage and state so we don't show GeneratingView again
            sessionStorage.removeItem(`trip_${tripData.trip.id}_questionType`);
            sessionStorage.removeItem(`trip_${tripData.trip.id}_editToken`);
            setPatchQuestionType(null);

            // If trip was already ready, patch was applied immediately - refetch to get patched content
            // so the main view shows the updated itinerary when we switch.
            await fetchTrip();
        } catch (err) {
            logger.error('Failed to submit patch:', err);
            // Still show loading modal - the trip generation continues
        }
    }, [tripData?.trip.id, patchQuestionType, editToken, fetchTrip]);

    // Polling and confirmation for post-checkout
    const [verifying, setVerifying] = useState(false);
    const hasCheckedUnlock = useRef(false);
    const hasCheckedPending = useRef(false);
    
    // Handle Stripe return parameters (session_id for success, canceled for back)
    useEffect(() => {
        // Only run once per page load
        if (hasCheckedUnlock.current) return;
        
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const unlocked = urlParams.get('unlocked');
        const canceled = urlParams.get('canceled');
        
        // Success - Stripe returned with session_id or unlocked=true
        if (sessionId || unlocked === 'true') {
            hasCheckedUnlock.current = true;
            setVerifying(true);
            clearCheckoutInFlight();
            
            // Clear URL immediately to prevent loops
            window.history.replaceState({}, '', window.location.pathname);
            
            const poll = async (delay: number, attempt: number) => {
                await new Promise(r => setTimeout(r, delay));
                const status = await refreshStatus();
                if (status?.isPremium || status?.isTripUnlocked) {
                    // Payment verified! Now clone the trip and redirect to the user's copy
                    try {
                        const result = await cloneTrip(slug);
                        if (result.success && result.slug) {
                            // Redirect to user's own editable clone
                            window.location.href = `/trip/${result.slug}`;
                            return;
                        }
                    } catch (cloneError) {
                        logger.error('Failed to clone trip after payment', { error: cloneError });
                    }
                    // If clone fails, at least show the unlocked trip
                    setVerifying(false);
                    return;
                }
                if (attempt < 3) {
                    poll(attempt === 0 ? 1000 : 2000, attempt + 1);
                } else {
                    setVerifying(false);
                    // Reload page as last resort
                    window.location.reload();
                }
            };
            poll(0, 0);
            return;
        }
        
        // Canceled - User clicked back from Stripe
        if (canceled === '1') {
            hasCheckedUnlock.current = true;
            clearCheckoutInFlight();
            window.history.replaceState({}, '', window.location.pathname);
            
            // Open modal with canceled message
            setCheckoutCanceled(true);
            setForceUpgradeView(true);
            setModalEntrypoint('cta');
            setShowUnlockModal(true);
        }
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-checkout from pending checkout (after OAuth redirect)
    useEffect(() => {
        if (hasCheckedPending.current) return;
        if (!tripData?.trip.id || !userStatus) return;
        
        hasCheckedPending.current = true;
        
        const pending = getPendingCheckout();
        
        // Guard conditions
        if (!pending) return;
        if (!userStatus.isAuthenticated) return;
        
        // Check if trip already unlocked
        if (userStatus.isPremium || userStatus.isTripUnlocked) {
            clearPendingCheckout();
            // Already unlocked - could have paid before
            return;
        }
        
        // Check if pending is for this trip
        if (pending.tripSlug !== slug) {
            clearPendingCheckout();
            return;
        }
        
        // Check if checkout already in flight
        if (isCheckoutInFlight()) {
            return;
        }
        
        // Check if expired
        if (isPendingCheckoutExpired()) {
            clearPendingCheckout();
            setSessionExpired(true);
            setForceUpgradeView(true);
            setModalEntrypoint('cta');
            setShowUnlockModal(true);
            return;
        }
        
        // All guards passed - trigger auto-checkout
        setCheckoutInFlight(pending.intentId);
        clearPendingCheckout();
        
        createTripUnlockCheckout(pending.tripSlug)
            .then(result => {
                if (result.sessionUrl) {
                    window.location.href = result.sessionUrl;
                }
            })
            .catch(() => {
                clearCheckoutInFlight();
                // Show modal with error
                setForceUpgradeView(true);
                setModalEntrypoint('cta');
                setShowUnlockModal(true);
            });
    }, [tripData?.trip.id, userStatus, slug]);

    // Handlers
    const handleShare = () => {
        setShowShareModal(true);
    };

    const handleClone = async () => {
        if (!userStatus?.isAuthenticated) {
            setModalTrigger('customize');
            setShowUnlockModal(true);
            return;
        }

        setCloning(true);
        try {
            const result = await cloneTrip(slug);
            if (result.success) {
                window.location.href = `/trip/${result.slug}`;
            }
        } catch (err) {
            logger.error('Failed to clone trip:', err);
            alert(t('trip.failed_clone'));
        } finally {
            setCloning(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!tripData?.content) return;
        
        // Only premium users or users who have unlocked this trip can download PDF
        if (!userStatus?.isPremium && !userStatus?.isTripUnlocked) {
            setModalTrigger('pdf');
            setShowUnlockModal(true);
            return;
        }

        try {
            const response = await fetch(`/api/trip/${slug}/pdf`, {
                method: 'GET',
                credentials: 'include',
            });

            if (response.status === 403) {
                setModalTrigger('pdf');
                setShowUnlockModal(true);
                return;
            }

            if (!response.ok) {
                throw new Error('PDF generation failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${tripData.content.title.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 50)}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            logger.error('PDF download failed:', error);
            alert(t('trip.pdf_failed'));
        }
    };

    // Initial loading state
    if (tripLoading || verifying) {
        return (
            <div className="trip-loading-view">
                <div className="loading-content">
                    <div className="loading-spinner" />
                    <p>{verifying ? t('trip.updating_access') : t('trip.loading_itinerary')}</p>
                </div>
            </div>
        );
    }

    // Trip not found
    if (tripError || !tripData) {
        return <TripNotFound />;
    }

    const { content, trip } = tripData;

    // Show generating view if trip is still being created OR we have a pending patch question
    // (e.g. user just came from TripBuilderForm and must pick one of the 3 pace options).
    // Without this, when the worker finishes very fast we'd show the main view and skip the pace picker.
    const hasPendingPatchQuestion = Boolean(patchQuestionType && !hasAnsweredPatch);
    const showGeneratingView = (isGenerating || !content) || hasPendingPatchQuestion;

    if (showGeneratingView) {
        return (
            <GeneratingView
                tripId={trip.id}
                slug={slug}
                patchQuestion={patchQuestion}
                hasAnswered={hasAnsweredPatch}
                showLoadingModal={showLoadingModal}
                onAnswer={handlePatchAnswer}
                onReady={() => {
                    setIsGenerating(false);
                    setShowLoadingModal(false);
                    fetchTrip();
                }}
            />
        );
    }

    const currentDayNumber = (() => {
        let num = 1;
        for (let c = 0; c < selectedDay.cityIdx; c++) {
            num += content.cities[c].days.length;
        }
        return num + selectedDay.dayIdx;
    })();

    const handleAddToCalendar = () => {
        // Guard: this action is only available for authenticated users
        if (!userStatus?.isAuthenticated) {
            setModalTrigger('signin');
            setShowUnlockModal(true);
            return;
        }

        const params = new URLSearchParams({
            action: 'add-vacation',
            tripId: trip.id,
            title: content.title,
            start: content.dateRange.start,
            end: content.dateRange.end,
        });
        navigate({ to: `/dashboard?${params.toString()}` as any });
    };

    // Handler for sticky bar unlock button
    const handleStickyUnlock = () => {
        setCheckoutCanceled(false);
        setSessionExpired(false);
        setForceUpgradeView(true);
        setModalEntrypoint('cta');
        setModalTrigger('banner');
        setShowUnlockModal(true);
    };

    return (
        <div className={`trip-container ${showStickyBar ? 'has-sticky-bar' : ''}`}>
            <TripBanner
                isTemplate={trip.isTemplate}
                isPublic={trip.isPublic}
                isAuthenticated={!!userStatus?.isAuthenticated}
                cloning={cloning}
                onClone={handleClone}
                onSignInToClaim={() => {
                    setModalTrigger('signin');
                    setShowUnlockModal(true);
                }}
            />

            {/* Mobile Layout: Map (32vh) + Summary Card + Inline Itinerary */}
            {isMobile ? (
                <main className="trip-layout-mobile">
                    {/* Map first - immediate visual context */}
                    <div className="mobile-map-container">
                        <MapPanel
                            content={content}
                            selectedDay={selectedDay}
                            currentDayNumber={currentDayNumber}
                        />
                    </div>

                    {/* Trip Summary Card - creates trust and clarity */}
                    <TripSummaryCard content={content} />
                    <div className="mobile-itinerary-container">
                        <ItinerarySidebar
                            content={content}
                            selectedDay={selectedDay}
                            onSelectDay={(cityIdx, dayIdx) => {
                                setSelectedDay({ cityIdx, dayIdx });
                                setIsChatOpenMobile(false);
                            }}
                            sidebarOpen={true}
                            onClose={() => {}}
                            tripId={trip.id}
                            userStatus={userStatus}
                            onShowUnlockModal={() => {
                                setModalTrigger('chat');
                                setForceUpgradeView(true);
                                setModalEntrypoint('edit_action');
                                setShowUnlockModal(true);
                            }}
                            onClone={handleClone}
                            onTripUpdated={fetchTrip}
                            onAddActivity={async (request, dayIndex, _cityName) => {
                                try {
                                    const result = await addActivity(trip.id, {
                                        description: request.description,
                                        dayIndex,
                                        cityIndex: selectedDay.cityIdx,
                                        time: request.time,
                                        period: request.period,
                                    });
                                    if (result.success) {
                                        fetchTrip();
                                    }
                                } catch (error) {
                                    logger.error('Failed to add activity:', error);
                                    throw error;
                                }
                            }}
                            variant="inline"
                            editToken={editToken || undefined}
                            itineraryAccess={tripData?.itineraryAccess}
                            onAddDay={async (cityIdx) => {
                                try {
                                    const r = await addDay(trip.id, cityIdx, editToken || urlEditToken);
                                    await fetchTrip();
                                    setSelectedDay({ cityIdx, dayIdx: r.newDayIndex });
                                } catch (error) {
                                    if (error instanceof AddDayError && error.code === 'NEED_UNLOCK') {
                                        setModalTrigger('customize');
                                        setForceUpgradeView(true);
                                        setModalEntrypoint('edit_action');
                                        setShowUnlockModal(true);
                                    } else {
                                        throw error;
                                    }
                                }
                            }}
                        />
                    </div>

                    <ChatPanel
                        tripSlug={slug}
                        userStatus={userStatus}
                        isTemplate={trip.isTemplate}
                        onShowUnlockModal={() => {
                            setModalTrigger('chat');
                            setForceUpgradeView(true);
                            setModalEntrypoint('customize');
                            setShowUnlockModal(true);
                        }}
                        onUpdateTripData={updateTripData}
                        onUserStatusUpdate={setUserStatus}
                        onClone={handleClone}
                        isOpenMobile={isChatOpenMobile}
                        onCloseMobile={() => setIsChatOpenMobile(false)}
                        editContext={(() => {
                            const day = content.cities[selectedDay.cityIdx]?.days[selectedDay.dayIdx];
                            return day && (day.activities?.length ?? 0) === 0
                                ? { emptyDay: true, cityIndex: selectedDay.cityIdx, dayIndex: selectedDay.dayIdx }
                                : undefined;
                        })()}
                    />

                    {isChatOpenMobile && <div className="chat-overlay-mobile" onClick={() => setIsChatOpenMobile(false)} />}

                    <button
                        className="mobile-chat-trigger-fab"
                        onClick={() => setIsChatOpenMobile(true)}
                        aria-label={t('trip.open_chat')}
                    >
                        <img src="/raisa.png" alt="Raisa" />
                    </button>
                </main>
            ) : (
                /* Desktop Layout: Clean 2-column (Itinerary | Chat), map floats on demand */
                <main className="trip-layout trip-layout-desktop">
                    {/* Main content grid - true 2-column (no TripHero - info already in TripHeader) */}
                    <div className="trip-content-grid">
                        <ItinerarySidebar
                            openRoutesModalRef={openRoutesModalRef}
                            content={content}
                            selectedDay={selectedDay}
                            onSelectDay={(cityIdx, dayIdx) => {
                                setSelectedDay({ cityIdx, dayIdx });
                                setIsChatOpenMobile(false);
                            }}
                            sidebarOpen={sidebarOpen}
                            onClose={() => setSidebarOpen(false)}
                            tripId={trip.id}
                            userStatus={userStatus}
                            onShowUnlockModal={() => {
                                setModalTrigger('chat');
                                setShowUnlockModal(true);
                            }}
                            onClone={handleClone}
                            onTripUpdated={fetchTrip}
                            onAddActivity={async (request, dayIndex, _cityName) => {
                                try {
                                    const result = await addActivity(trip.id, {
                                        description: request.description,
                                        dayIndex,
                                        cityIndex: selectedDay.cityIdx,
                                        time: request.time,
                                        period: request.period,
                                    });
                                    if (result.success) {
                                        fetchTrip();
                                    }
                                } catch (error) {
                                    logger.error('Failed to add activity:', error);
                                    throw error;
                                }
                            }}
                            variant="sidebar"
                            onViewOnMap={handleViewOnMap}
                            isMapOpen={isDesktopMapOpen}
                            onToggleMap={handleToggleMap}
                            onExport={handleDownloadPdf}
                            editToken={editToken || undefined}
                            itineraryAccess={tripData?.itineraryAccess}
                            onAddDay={async (cityIdx) => {
                                try {
                                    const r = await addDay(trip.id, cityIdx, editToken || urlEditToken);
                                    await fetchTrip();
                                    setSelectedDay({ cityIdx, dayIdx: r.newDayIndex });
                                } catch (error) {
                                    if (error instanceof AddDayError && error.code === 'NEED_UNLOCK') {
                                        setModalTrigger('customize');
                                        setForceUpgradeView(true);
                                        setModalEntrypoint('edit_action');
                                        setShowUnlockModal(true);
                                    } else {
                                        throw error;
                                    }
                                }
                            }}
                            heroSlot={<TripHero 
                                content={content} 
                                isLocked={isTripLocked} 
                                onToggleMap={handleToggleMap}
                                onExport={handleDownloadPdf}
                                onShare={handleShare}
                                onAddToCalendar={userStatus?.isAuthenticated ? handleAddToCalendar : undefined}
                                currentCityName={content.cities[selectedDay.cityIdx]?.name}
                                currentCityDuration={content.cities[selectedDay.cityIdx]?.days.length}
                                selectedCityIdx={selectedDay.cityIdx}
                                lastDayPerCity={{}}
                                onSelectCity={(cityIdx, dayIdx) => setSelectedDay({ cityIdx, dayIdx })}
                                onEditRoutes={() => openRoutesModalRef.current?.()}
                                isRouteEditable={!!userStatus?.isOwner || !!editToken}
                            />}
                        />

                        <ChatPanel
                            tripSlug={slug}
                            userStatus={userStatus}
                            isTemplate={trip.isTemplate}
                            onShowUnlockModal={() => {
                                setModalTrigger('chat');
                                setShowUnlockModal(true);
                            }}
                            onUpdateTripData={updateTripData}
                            onUserStatusUpdate={setUserStatus}
                            onClone={handleClone}
                            isOpenMobile={isChatOpenMobile}
                            onCloseMobile={() => setIsChatOpenMobile(false)}
                            editContext={(() => {
                                const day = content.cities[selectedDay.cityIdx]?.days[selectedDay.dayIdx];
                                return day && (day.activities?.length ?? 0) === 0
                                    ? { emptyDay: true, cityIndex: selectedDay.cityIdx, dayIndex: selectedDay.dayIdx }
                                    : undefined;
                            })()}
                        />
                    </div>

                    {/* Floating Map Panel - overlays itinerary area, never chat */}
                    <MapPanelFloating
                        isOpen={isDesktopMapOpen}
                        onClose={() => {
                            setIsDesktopMapOpen(false);
                            setSelectedActivityId(null);
                            setFocusedActivityTitle(undefined);
                        }}
                        content={content}
                        selectedDay={selectedDay}
                        currentDayNumber={currentDayNumber}
                        selectedActivityId={selectedActivityId}
                        onClearSelectedActivity={handleClearSelectedActivity}
                        focusedActivityTitle={focusedActivityTitle}
                    />

                    {isChatOpenMobile && <div className="chat-overlay-mobile" onClick={() => setIsChatOpenMobile(false)} />}

                    <button
                        className="mobile-chat-trigger-fab"
                        onClick={() => setIsChatOpenMobile(true)}
                        aria-label={t('trip.open_chat')}
                    >
                        <img src="/raisa.png" alt="Raisa" />
                    </button>
                </main>
            )}

            {/* Sticky Unlock Bar - Mobile only, when trip is locked */}
            <StickyUnlockBar
                isVisible={showStickyBar}
                onUnlock={handleStickyUnlock}
                isEligibleForLaunchPromo={userStatus?.isEligibleForLaunchPromo}
            />

            <UnlockModal
                isOpen={showUnlockModal}
                onClose={() => {
                    setShowUnlockModal(false);
                    setCheckoutCanceled(false);
                    setSessionExpired(false);
                }}
                userStatus={userStatus}
                tripSlug={slug}
                tripId={trip.id}
                trigger={modalTrigger}
                entrypoint={modalEntrypoint}
                forceUpgradeView={forceUpgradeView}
                checkoutCanceled={checkoutCanceled}
                sessionExpired={sessionExpired}
            />

            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                tripSlug={slug}
                tripTitle={content.title}
                isPublic={trip.isPublic}
                isPremium={!!userStatus?.isPremium}
                isOwner={!!userStatus?.isOwner}
                onVisibilityUpdated={(isPublic) => {
                    updateTripData({
                        trip: { ...tripData.trip, isPublic }
                    });
                }}
            />
        </div>
    );
}
